import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Snackbar, Tooltip } from '@mui/material';
import { IconRefresh, IconPlus, IconPencil } from '@tabler/icons-react';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton, ToolbarSearch } from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import type {
  ServiceCatalogItem,
  ServiceCatalogCreateRequest,
  ServiceCatalogUpdateRequest,
} from '@techgitter/contracts';
import {
  getServiceCatalog,
  createServiceCatalogItem,
  updateServiceCatalogItem,
} from '../services/customerServicesApi';
import { ServiceCatalogEditDialog } from '../components/admin/ServiceCatalogEditDialog';

/**
 * Admin → Service Catalog. Manage the customer-services catalog
 * (`cr001_serviceses`) — the list the Add Service picker on a customer draws
 * from, and the `serviceKey` space that ticket-type template visibility keys
 * off. Mirrors the Repair Services admin page (DataTable kit + modal editor).
 */
export const CustomerServicesCatalogAdminPage: React.FC = () => {
  const [items, setItems] = useState<ServiceCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const [editing, setEditing] = useState<ServiceCatalogItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setLoadError(null);
      // activeOnly=false so admins can see and reactivate soft-deleted rows.
      const data = await getServiceCatalog({ activeOnly: false }, signal);
      setItems(data.items ?? []);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setLoadError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const dialogOpen = creating || editing !== null;

  const closeDialog = () => {
    if (saving) return;
    setCreating(false);
    setEditing(null);
    setSaveError(null);
  };

  const existingKeys = useMemo(
    () => items.map((it) => it.serviceKey.toLowerCase()),
    [items],
  );

  const handleSave = async (
    payload: ServiceCatalogCreateRequest | ServiceCatalogUpdateRequest,
    serviceId: string | null,
  ) => {
    setSaving(true);
    setSaveError(null);
    try {
      if (serviceId === null) {
        const created = await createServiceCatalogItem(payload as ServiceCatalogCreateRequest);
        setItems((current) => [...current, created]);
        setSnackbar({ open: true, message: 'Service created', severity: 'success' });
      } else {
        const updated = await updateServiceCatalogItem(serviceId, payload as ServiceCatalogUpdateRequest);
        setItems((current) => current.map((it) => (it.serviceId === updated.serviceId ? updated : it)));
        setSnackbar({ open: true, message: 'Service updated', severity: 'success' });
      }
      setCreating(false);
      setEditing(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const sortedItems = useMemo(() => {
    const query = filter.trim().toLowerCase();
    const filtered = query
      ? items.filter((item) =>
          [item.serviceKey, item.displayName].filter(Boolean).join(' ').toLowerCase().includes(query),
        )
      : items;
    return [...filtered].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName),
    );
  }, [items, filter]);

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSearch
          searchValue={filter}
          onSearchChange={setFilter}
          searchPlaceholder="Filter by key or name…"
        />
        <ToolbarSpacer />
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
        <ToolbarButton
          variant="primary"
          icon={IconPlus}
          onClick={() => {
            setSaveError(null);
            setCreating(true);
          }}
        >
          Add service
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', minWidth: 0 }}>
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTableCard>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell width={320}>Display name</DataTableHeadCell>
                  <DataTableHeadCell>Service key</DataTableHeadCell>
                  <DataTableHeadCell align="right">Sort order</DataTableHeadCell>
                  <DataTableHeadCell>Active</DataTableHeadCell>
                  <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <DataTableEmpty colSpan={5}>
                    {filter.trim() ? 'No services match your filter' : 'No services configured'}
                  </DataTableEmpty>
                ) : (
                  sortedItems.map((item) => (
                    <DataTableRow key={item.serviceId}>
                      <DataTableCell>{item.displayName || '—'}</DataTableCell>
                      <DataTableCell>
                        <span style={{ fontFamily: 'monospace' }}>{item.serviceKey || '—'}</span>
                      </DataTableCell>
                      <DataTableCell align="right">{item.sortOrder}</DataTableCell>
                      <DataTableCell>
                        <Chip tone={item.isActive ? 'success' : 'neutral'}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </Chip>
                      </DataTableCell>
                      <DataTableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSaveError(null);
                              setEditing(item);
                            }}
                          >
                            <IconPencil size={16} stroke={1.5} />
                          </IconButton>
                        </Tooltip>
                      </DataTableCell>
                    </DataTableRow>
                  ))
                )}
              </tbody>
            </DataTable>
          </DataTableCard>
        )}
      </Box>

      <ServiceCatalogEditDialog
        open={dialogOpen}
        service={editing}
        existingKeys={existingKeys}
        isSaving={saving}
        error={saveError}
        onClose={closeDialog}
        onSave={handleSave}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
